/**
 * Admin: set a member's email from the daftar anggota action menu.
 */

import * as React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { userService } from "@/services/userService";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SetEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName?: string;
  callsign?: string;
  /** Email currently on file; shown first and used as the initial field value */
  currentEmail: string;
  onSuccess?: () => void;
}

export function SetEmailDialog({
  open,
  onOpenChange,
  userId,
  userName,
  callsign,
  currentEmail,
  onSuccess,
}: SetEmailDialogProps) {
  const [emailInput, setEmailInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setEmailInput((currentEmail || "").trim());
      setLoading(false);
    }
  }, [open, currentEmail]);

  const normalizedCurrent = (currentEmail || "").trim().toLowerCase();
  const normalizedInput = emailInput.trim().toLowerCase();
  const isValid = EMAIL_RE.test(normalizedInput);
  const unchanged = normalizedInput === normalizedCurrent;
  const canSubmit = isValid && !unchanged && !loading;

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    try {
      await userService.setUserEmail(userId, normalizedInput);
      toast.success("Email berhasil diperbarui");
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Gagal memperbarui email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Set Email</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-1 text-sm text-muted-foreground">
              {userName && (
                <p>
                  {callsign ? `${callsign} · ${userName}` : userName}
                </p>
              )}
              <p>
                <span className="font-medium text-foreground">Email saat ini: </span>
                {currentEmail?.trim() ? currentEmail.trim() : "—"}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="set-email-input">Email baru</Label>
          <Input
            id="set-email-input"
            type="email"
            autoComplete="off"
            value={emailInput}
            onChange={(ev) => setEmailInput(ev.target.value)}
            placeholder="nama@contoh.com"
            disabled={loading}
            className="text-sm"
          />
          {emailInput.trim() && !isValid && (
            <p className="text-xs text-destructive">Format email tidak valid</p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>Batal</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Menyimpan...
              </>
            ) : (
              "Simpan"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
